import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { style } from './expandable.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { fontStyles } from 'elements/_shared/fonts.style.js'
import { generateID } from 'elements/_shared/generate-id.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'


const translations = {
  showMore: () => t('show_more', { ns: 'cse-expandable', default: 'Show more' }),
  showLess: () => t('show_less', { ns: 'cse-expandable', default: 'Show less' }),
  showMoreIncremental: (count: number) => t('show_more_incremental', {
    ns: 'cse-expandable',
    default: 'Show {{variable_count}} more',
    variable_count: count,
  }),
}


@customElement('cse-expandable')
export class CSEExpandable extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, fontStyles, style]

  @property({ type: String })
  variant: 'text' | 'list' | 'list-incremental' = 'text'

  @property({ type: Number, attribute: 'visible-on-init' })
  visibleOnInit = 100

  @property({ type: Number, attribute: 'batch-size' })
  batchSize = 5

  @property({ type: Boolean })
  open = false

  @query('slot')
  private defaultSlot!: HTMLSlotElement

  @query('.content')
  private contentSlot!: HTMLSlotElement

  @state()
  toggleableChildren: Array<HTMLElement> = []

  @state()
  displayToggle: boolean = true

  @state()
  visibleCount = 0

  @state()
  private totalCount = 0

  @state()
  private textOverflows = false

  private textResizeObserver: ResizeObserver | null = null

  private allowedChildNodes = [
    'CSE-CHECKBOX-GROUP',
    'CSE-RADIO-BUTTON-GROUP',
    'CSE-GROUP',
    'CSE-LIST'
  ]

  private contentId = generateID('expandable')

  private childrenObserver: MutationObserver | null = null

  get nextBatch(): number {
    return Math.max(0, Math.min(this.batchSize, this.totalCount - this.visibleCount))
  }

  private get hasToggle(): boolean {
    if (this.variant === 'text') return this.open || this.textOverflows
    return this.displayToggle
  }

  private get displayShowMore(): boolean {
    return this.variant === 'list-incremental' && this.visibleCount < this.totalCount
  }

  protected override firstUpdated() {
    this.defaultSlot.addEventListener('slotchange', this.handleSlotChange)
    this.handleSlotChange()
  }

  override connectedCallback() {
    super.connectedCallback()

    if (!this.hasUpdated) { return }

    this.defaultSlot.addEventListener('slotchange', this.handleSlotChange)
    this.handleSlotChange()
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    this.childrenObserver?.disconnect()
    this.textResizeObserver?.disconnect()
    this.defaultSlot?.removeEventListener('slotchange', this.handleSlotChange)
  }

  private getListNodes(): HTMLElement[] {
    return this.defaultSlot.assignedNodes({ flatten: true })
      .filter(node => this.allowedChildNodes.includes(node.nodeName)) as HTMLElement[]
  }

  private getListChildren(): HTMLElement[] {
    return this.getListNodes().flatMap(node => [...node.children] as HTMLElement[])
  }

  private handleSlotChange = () => {
    this.childrenObserver?.disconnect()
    this.toggleableChildren = []

    if (this.variant === 'list' || this.variant === 'list-incremental') {
      const listNodes = this.defaultSlot.assignedNodes({ flatten: true })
      const filteredListNodes = listNodes.filter(node => this.allowedChildNodes.includes(node.nodeName))

      filteredListNodes.forEach((node: Node) => {
        const children = [...(node as HTMLElement).children]
        children.forEach((child, index) => {
          if (index >= this.visibleOnInit) {
            this.toggleableChildren.push(child as HTMLElement)
          }
        })
      })

      if (filteredListNodes.length > 0) {
        const observer = new MutationObserver(this.handleSlotChange)
        filteredListNodes.forEach(node => observer.observe(node, { childList: true }))
        this.childrenObserver = observer
      }

      this.totalCount = this.getListChildren().length

      if (this.variant === 'list-incremental') {
        this.visibleCount = this.visibleCount === 0
          ? Math.min(this.visibleOnInit, this.totalCount)
          : Math.min(this.visibleCount, this.totalCount)
        this.dispatchVisibleCountChange()
      }

      this.displayToggle = this.toggleableChildren.length > 0
    }

    if (this.variant === 'text') {
      this.observeTextSize()
    }

    this.updateVisibility()
  }

  protected override updated() {
    if (this.variant === 'text') {
      this.measureTextOverflow()
    }
  }

  private observeTextSize() {
    this.textResizeObserver?.disconnect()
    this.textResizeObserver = new ResizeObserver(() => this.measureTextOverflow())
    this.textResizeObserver.observe(this.contentSlot)
    this.defaultSlot.assignedElements({ flatten: true }).forEach(element => this.textResizeObserver?.observe(element))
  }

  private measureTextOverflow() {
    if (this.open || !this.contentSlot) return
    this.textOverflows = this.contentSlot.scrollHeight > this.contentSlot.clientHeight + 1
  }

  private updateVisibility = () => {
    if (this.variant === 'list') {
      this.getListNodes().forEach((node) => {
        const children = [...node.children] as HTMLElement[]
        children.forEach((child, index) => {
          const shouldHide = index >= this.visibleOnInit && !this.open
          child.style.setProperty('display', shouldHide ? 'none' : '')
        })
      })
      return
    }

    if (this.variant === 'list-incremental') {
      const children = this.getListChildren()
      children.forEach((child, index) => {
        child.style.setProperty('display', index >= this.visibleCount ? 'none' : '')
      })
    }
  }

  private preventLinkDefault(event: Event) {
    const targetTag = (event.target as HTMLElement).tagName
    if (targetTag === 'A' || targetTag === 'CSE-LINK') {
      event.preventDefault()
    }
  }

  private toggle = (event: Event) => {
    this.preventLinkDefault(event)
    this.open = !this.open
    this.updateVisibility()
  }

  private handleShowMore = (event: Event) => {
    this.preventLinkDefault(event)
    this.visibleCount = Math.min(this.visibleCount + this.batchSize, this.totalCount)
    this.updateVisibility()
    this.dispatchVisibleCountChange()
  }

  private dispatchVisibleCountChange() {
    this.dispatchEvent(new CustomEvent('visible-count-change', {
      bubbles: true,
      composed: true,
      detail: {
        visible: this.visibleCount,
        total: this.totalCount,
        nextBatch: this.nextBatch,
      },
    }))
  }

  private renderTextContentStyle = () => {
    if (!this.open) {
      return `max-height: var(--cse-expandable-content-height, ${this.visibleOnInit}px);`
    }
    return 'max-height: none;'
  }

  private renderToggle = () => {
    if (this.open) {
      return html`
        <slot name="toggle-close" @click=${this.toggle} class="toggle" aria-expanded=${this.open} aria-controls=${this.contentId}>
          <cse-link>${translations.showLess()}</cse-link>
        </slot>
      `
    } else {
      return html`
        <slot name="toggle-open" @click=${this.toggle} class="toggle" aria-expanded=${this.open} aria-controls=${this.contentId}>
          <cse-link>${translations.showMore()}</cse-link>
        </slot>
      `
    }
  }

  private renderIncrementalToggles = () => {
    if (!this.displayShowMore) return nothing
    return html`
      <slot name="toggle-open" @click=${this.handleShowMore} class="toggle toggle-more" aria-controls=${this.contentId}>
        <cse-link>${translations.showMoreIncremental(this.nextBatch)}</cse-link>
      </slot>
    `
  }

  override render() {
    return html`
      <section
        class=${classMap({
          'cse-expandable': true,
          'open': this.open,
          'overflows': this.textOverflows,
          [`variant-${this.variant}`]: true
        })}>
        <slot
          class="content"
          style="${this.variant==='text' ? this.renderTextContentStyle() : ''}"
          id=${this.contentId}
        ></slot>
        ${this.variant === 'list-incremental'
          ? this.renderIncrementalToggles()
          : (this.hasToggle ? this.renderToggle() : nothing)}
      </section>
    `
  }
}


declare global {
  interface HTMLElementTagNameMap {
    'cse-expandable': CSEExpandable
  }
}
